<script setup lang="ts">
import type { PropType } from "vue"
import type { Media } from "@/models"
import { computed } from "vue"
import { BaseModal, Button, Card, Chip, Icon, ReadMore } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { MediaDetailsModalKey } from "@/symbols"

const props = defineProps({
  media: {
    type: Object as PropType<Media>,
    required: true,
  },
  isHost: {
    type: Boolean,
    default: false,
  },
  isTaggingEnabled: {
    type: Boolean,
    default: false,
  },
})

const { d, t } = useTranslation()
const displayTags = computed(() => {
  return props.isTaggingEnabled && props.isHost && props.media.tags?.length
})

const { toggle } = useModal(MediaDetailsModalKey)
</script>

<template>
  <BaseModal :name="MediaDetailsModalKey">
    <Card>
      <template #header>
        <div class="gpx-media-details-header">
          <h2 class="font-semibold text-xl">
            {{ media?.title || "Untitled Media" }}
          </h2>
          <Button variant="flat" @click="toggle">
            <span class="sr-only">{{ t("media_details_modal.close") }}</span>
            <Icon icon="Close" />
          </Button>
        </div>
        <dl>
          <div v-if="media.description" class="w-full border-0 border-t border-t-gray-300 gpx-media-detail-section">
            <dt>{{ t("description") }}</dt>
            <dd><ReadMore :content="media.description" :lines="5" /></dd>
          </div>
          <div v-if="media.uploadedBy" class="w-full border-0 border-t border-t-gray-300 gpx-media-detail-section">
            <dt>{{ t("uploaded_by") }}</dt>
            <dd>{{ media.uploadedBy }}</dd>
          </div>
          <div v-if="media.createdAt" class="w-full border-0 border-t border-t-gray-300 gpx-media-detail-section">
            <dt>{{ t("uploaded_on") }}</dt>
            <dd>{{ d(media.createdAt, "short") }}</dd>
          </div>
          <div v-if="media.fileName" class="w-full border-0 border-t border-t-gray-300 gpx-media-detail-section">
            <dt>{{ t("file_name") }}</dt>
            <dd>{{ media.fileName }}</dd>
          </div>
          <div v-if="displayTags" class="w-full border-0 border-t border-t-gray-300 gpx-media-detail-section">
            <dt>{{ t("media_details_modal.tags") }}</dt>
            <dd>
              <div class="flex flex-wrap gap-2">
                <Chip v-for="(tag, index) in media.tags" :key="index" :label="tag" is-tag />
              </div>
            </dd>
          </div>
        </dl>
      </template>
    </Card>
  </BaseModal>
</template>

<style lang="postcss" scoped>
@reference "../../assets/styles.css";

.gpx-media-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.gpx-media-details-header button,
.gpx-media-details-header h2 {
  padding: 0;
  margin-bottom: 1rem;
}

.gpx-media-detail-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 0;
}

.gpx-media-detail-section dt {
  font-size: 0.875rem;
  line-height: 1.5rem;
  min-width: 20%;
}

@media (width >= 768px) {
  .gpx-media-detail-section {
    flex-direction: row;
  }
}
</style>
